import UnifiedSEO from '@/components/seo/UnifiedSEO';
import { usePublicRoadmap, RoadmapItem, RoadmapStatus } from '@/hooks/useRoadmap';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  ShieldAlert, Heart, Users, Globe, Lightbulb, Loader2,
  CircleDot, CalendarCheck, Hammer, CheckCircle2,
} from 'lucide-react';

const STATUS_CONFIG: Record<RoadmapStatus, {
  label: string;
  color: string;
  bgColor: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = {
  considering: {
    label: 'Considering',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100 border-gray-200',
    Icon: CircleDot,
  },
  planned: {
    label: 'Planned',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50 border-blue-200',
    Icon: CalendarCheck,
  },
  in_development: {
    label: 'In Development',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50 border-amber-200',
    Icon: Hammer,
  },
  shipped: {
    label: 'Shipped',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50 border-emerald-200',
    Icon: CheckCircle2,
  },
};

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  safety: ShieldAlert,
  wellness: Heart,
  family: Users,
  community: Globe,
};

const STATUS_ORDER: RoadmapStatus[] = ['considering', 'planned', 'in_development', 'shipped'];

function groupByStatus(items: RoadmapItem[]) {
  const grouped: Record<RoadmapStatus, RoadmapItem[]> = {
    considering: [],
    planned: [],
    in_development: [],
    shipped: [],
  };
  for (const item of items) {
    grouped[item.status]?.push(item);
  }
  return grouped;
}

function FeatureCard({ item }: { item: RoadmapItem }) {
  const CategoryIcon = CATEGORY_ICONS[item.category] || Lightbulb;
  const shippedDate = item.shipped_at
    ? new Date(item.shipped_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;

  return (
    <Card className="border shadow-raised">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg shrink-0">
            <CategoryIcon className="h-4 w-4 text-primary" />
          </div>
          <div className="space-y-1 min-w-0">
            <h3 className="font-bold text-foreground leading-snug">{item.title}</h3>
            {item.description && (
              <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs capitalize">{item.category}</Badge>
          {shippedDate && (
            <span className="text-xs text-muted-foreground">Shipped {shippedDate}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusColumn({ status, items }: { status: RoadmapStatus; items: RoadmapItem[] }) {
  const config = STATUS_CONFIG[status];
  const { Icon } = config;

  return (
    <div className="space-y-4">
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${config.bgColor}`}>
        <Icon className={`h-4 w-4 ${config.color}`} />
        <h2 className={`font-bold text-sm ${config.color}`}>{config.label}</h2>
        <span className={`text-xs ${config.color} opacity-70`}>({items.length})</span>
      </div>
      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item) => (
            <FeatureCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-8 italic">Nothing here yet</p>
      )}
    </div>
  );
}

export default function WhatsComingPage() {
  const { data: items, isLoading } = usePublicRoadmap();
  const grouped = groupByStatus(items || []);

  return (
    <>
      <UnifiedSEO
        title="What's Coming, SeniorThrive Feature Roadmap"
        description="See what features SeniorThrive is building next. Our public roadmap shows what we're considering, planning, and actively developing."
        canonical="https://seniorthrive.com/whats-coming"
        breadcrumbs={[
          { name: 'Home', url: 'https://seniorthrive.com/' },
          { name: "What's Coming", url: 'https://seniorthrive.com/whats-coming' },
        ]}
      />

      <div className="min-h-screen">
        {/* Hero */}
        <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              What's Coming to <span className="text-primary">SeniorThrive</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              See what we're building to help you thrive at home.
              These are features we're considering, planning, and actively working on.
            </p>
          </div>
        </section>

        {/* Board */}
        <section className="pb-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {STATUS_ORDER.map((status) => (
                  <StatusColumn key={status} status={status} items={grouped[status]} />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
